import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, Users, Pencil, Check, X } from 'lucide-react';
import { useSalesTeamsStore } from '../../store/salesTeamsStore';
import api from '../../lib/api';
import type { SalesTeamDetail, SalesTeamRole } from '../../types/salesTeam';
import type { User } from '../../types';

interface PickableUser {
  id: string;
  displayName: string | null;
  email: string;
}

export function SalesTeamsTab() {
  const { teams, loading, loadAll, create, remove, update } = useSalesTeamsStore();
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<SalesTeamDetail | null>(null);
  const [allUsers, setAllUsers] = useState<PickableUser[]>([]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  useEffect(() => {
    void loadAll();
    // /api/admin/users returns { users } and validates isActive as a string
    // ('true' | 'false' | 'all') with pageSize capped at 100. Reps/managers
    // are an internal, small set, so a single page of 100 active users is
    // more than enough to populate the roster picker.
    void api
      .get('/api/admin/users', { params: { isActive: 'true', pageSize: 100 } })
      .then((r) => {
        const users = (r.data.users ?? r.data.entities ?? []) as User[];
        setAllUsers(users.map((u) => ({ id: u.id, displayName: u.displayName, email: u.email })));
      })
      .catch(() => setAllUsers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await create({ name: newName.trim() });
      setNewName('');
      toast.success('Team created');
    } catch {
      toast.error('Failed to create team');
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await remove(id);
      toast.success('Team removed');
    } catch {
      toast.error('Failed to remove team');
    }
  };

  const startRename = (id: string, name: string) => {
    setRenamingId(id);
    setRenameValue(name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const saveRename = async (id: string, currentName: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    if (trimmed === currentName) {
      cancelRename();
      return;
    }
    try {
      await update(id, { name: trimmed });
      toast.success('Team renamed');
      cancelRename();
    } catch {
      toast.error('Failed to rename team');
    }
  };

  const openEditor = async (id: string) => {
    try {
      setEditing(await useSalesTeamsStore.getState().getOne(id));
    } catch {
      toast.error('Failed to load roster');
    }
  };

  const roleOf = (userId: string): SalesTeamRole | null => {
    const m = editing?.members.find((x) => x.userId === userId);
    return m ? m.role : null;
  };

  const setRole = (userId: string, role: SalesTeamRole | null) => {
    if (!editing) return;
    const members = editing.members.filter((m) => m.userId !== userId);
    if (role) {
      const u = allUsers.find((x) => x.id === userId);
      members.push({ userId, role, user: { displayName: u?.displayName ?? null, email: u?.email ?? '' } });
    }
    setEditing({ ...editing, members });
  };

  const saveRoster = async () => {
    if (!editing) return;
    try {
      await useSalesTeamsStore
        .getState()
        .setMembers(editing.id, { members: editing.members.map((m) => ({ userId: m.userId, role: m.role })) });
      toast.success('Roster saved');
      setEditing(null);
    } catch {
      toast.error('Failed to save roster');
    }
  };

  return (
    <div className="space-y-6">
      {/* Create */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Create a team</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Team name (e.g. West Division)"
            className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
          />
          <button onClick={handleCreate} className="btn-primary inline-flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> Add team
          </button>
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Teams</h3>
        </div>
        {loading && <div className="p-6 text-sm text-gray-400">Loading…</div>}
        {!loading && teams.length === 0 && <div className="p-6 text-sm text-gray-400">No teams yet.</div>}
        <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
          {teams.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 px-4 py-3">
              {renamingId === t.id ? (
                <>
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void saveRename(t.id, t.name);
                      if (e.key === 'Escape') cancelRename();
                    }}
                    aria-label={`Team name for ${t.name}`}
                    className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                  />
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => void saveRename(t.id, t.name)}
                      disabled={!renameValue.trim()}
                      className="text-blast-600 dark:text-blast-400 p-2 hover:bg-blast-50 dark:hover:bg-blast-900/20 rounded-lg disabled:opacity-40"
                      aria-label="Save name"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelRename}
                      className="text-gray-500 dark:text-gray-400 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      aria-label="Cancel rename"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{t.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t.memberCount} reps · {t.managerCount} manager{t.managerCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => startRename(t.id, t.name)}
                      className="text-gray-500 dark:text-gray-400 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      aria-label={`Rename ${t.name}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEditor(t.id)} className="btn-secondary inline-flex items-center gap-1 text-sm">
                      <Users className="w-4 h-4" /> Roster
                    </button>
                    <button
                      onClick={() => handleRemove(t.id)}
                      className="text-rose-600 dark:text-rose-400 p-2 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                      aria-label={`Delete ${t.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Roster editor */}
      {editing && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Roster — {editing.name}</h3>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={saveRoster} className="btn-primary text-sm">Save roster</button>
            </div>
          </div>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {allUsers.map((u) => {
              const role = roleOf(u.id);
              return (
                <li key={u.id} className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-900 dark:text-gray-100">{u.displayName || u.email}</span>
                  <select
                    value={role ?? 'none'}
                    onChange={(e) => setRole(u.id, e.target.value === 'none' ? null : (e.target.value as SalesTeamRole))}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm"
                  >
                    <option value="none">—</option>
                    <option value="TEAM_MEMBER">Member</option>
                    <option value="TEAM_MANAGER">Manager</option>
                  </select>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
